// components/contracts/ContractValidationCard.js
// Card stato validazioni con pulsanti espliciti e checklist documenti

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { 
  Calculator, ClipboardCheck, CheckCircle, AlertCircle, Clock,
  FileText, Shield, Loader2, AlertTriangle, User, Calendar,
  Lock
} from 'lucide-react';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/utils';

const VALIDATION_STATUS_CONFIG = {
  approvato: { label: 'Approvato', color: 'text-green-600', bg: 'bg-green-100', icon: CheckCircle },
  respinto: { label: 'Richieste Integrazioni', color: 'text-amber-600', bg: 'bg-amber-100', icon: AlertCircle },
  null: { label: 'Da validare', color: 'text-gray-500', bg: 'bg-gray-100', icon: Clock },
};

/**
 * Formatta la data di validazione in formato italiano
 */
const formatValidationDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    const date = new Date(dateStr);
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return dateStr;
  }
};

const ContractValidationCard = ({
  contract,
  contractId,
  canValidateComputo,
  canValidateAdminDocs,
  canShowValidationButtons,
  onOpenValidationModal
}) => {
  const [readiness, setReadiness] = useState(null);
  const [loadingReadiness, setLoadingReadiness] = useState(true);
  const [validatorNames, setValidatorNames] = useState({});

  const computoStatus = VALIDATION_STATUS_CONFIG[contract.computo_validation_status] || VALIDATION_STATUS_CONFIG['null'];
  const adminDocsStatus = VALIDATION_STATUS_CONFIG[contract.admin_docs_validation_status] || VALIDATION_STATUS_CONFIG['null'];
  
  const ComputoIcon = computoStatus.icon;
  const AdminDocsIcon = adminDocsStatus.icon;

  // Carica lo stato readiness e i nomi dei validatori
  useEffect(() => {
    const loadData = async () => {
      if (!contractId) return;
      
      try {
        setLoadingReadiness(true);
        
        // Carica readiness
        const readinessRes = await api.get(`/contracts/${contractId}/validation-readiness`);
        setReadiness(readinessRes.data);
        
        // Carica nomi validatori se presenti
        const validatorIds = [
          contract.computo_validated_by,
          contract.admin_docs_validated_by
        ].filter(Boolean);
        
        if (validatorIds.length > 0) {
          try {
            // Carica info utenti
            const usersRes = await api.get('/users');
            const usersMap = {};
            usersRes.data.forEach(user => {
              usersMap[user.id] = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email;
            });
            setValidatorNames(usersMap);
          } catch (err) {
            console.warn('Could not load validator names:', err);
          }
        }
      } catch (error) {
        console.error('Error loading validation readiness:', error);
        setReadiness(null);
      } finally {
        setLoadingReadiness(false);
      }
    };

    loadData();
  }, [contractId, contract.admin_docs_validation_status, contract.computo_validation_status, contract.computo_validated_by, contract.admin_docs_validated_by]);

  // Determina se il pulsante admin_docs deve essere abilitato
  const canClickAdminDocsButton = readiness?.can_validate_admin_docs ?? true;
  
  // Il pulsante computo è sempre cliccabile se l'utente ha i permessi e il contratto è nello stato giusto
  const canClickComputoButton = true;
  
  // Checklist items per documenti amministrativi
  const checklistItems = readiness ? [
    {
      label: 'PDF Contratto generato',
      done: readiness.has_pdf_url,
      icon: FileText
    },
    {
      label: 'Contratto firmato caricato',
      done: readiness.contratto_firmato_uploaded,
      icon: FileText
    },
    {
      label: 'Contratto firmato validato',
      done: readiness.contratto_firmato_validated,
      icon: CheckCircle
    },
    {
      label: 'Documento identità completo',
      done: readiness.documento_identita_uploaded,
      icon: Shield,
      subtitle: readiness.documento_identita_details?.legacy_uploaded 
        ? '(file unico)' 
        : readiness.documento_identita_details?.fronte_uploaded && readiness.documento_identita_details?.retro_uploaded
          ? '(fronte + retro)'
          : null
    },
    {
      label: 'Documento identità validato',
      done: readiness.documento_identita_validated,
      icon: CheckCircle
    }
  ] : [];
  
  // Helper per ottenere nome validatore
  const getValidatorName = (userId) => {
    if (!userId) return null;
    return validatorNames[userId] || 'Utente';
  };

  return (
    <Card className="card-abitalo">
      <CardHeader>
        <CardTitle className="text-lg">Stato Validazioni</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* === SEZIONE 1: VALIDAZIONE COMPUTO METRICO === */}
        <div className={`p-4 rounded-lg ${computoStatus.bg}`} data-testid="computo-validation">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Calculator className={`w-5 h-5 ${computoStatus.color}`} />
              <span className="font-medium text-sm">Computo Metrico</span>
            </div>
            <div className="flex items-center gap-1">
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${computoStatus.bg} ${computoStatus.color}`}>
                {computoStatus.label}
              </span>
              <ComputoIcon className={`w-4 h-4 ${computoStatus.color}`} />
            </div>
          </div>
          
          {/* Info validazione se approvato */}
          {contract.computo_validation_status === 'approvato' && (
            <div className="mt-3 p-2 bg-white/60 rounded text-xs space-y-1">
              {contract.computo_validated_at && (
                <div className="flex items-center gap-1.5 text-green-700">
                  <Calendar className="w-3 h-3" />
                  <span>Validato il: {formatValidationDate(contract.computo_validated_at)}</span>
                </div>
              )}
              {contract.computo_validated_by && (
                <div className="flex items-center gap-1.5 text-green-700">
                  <User className="w-3 h-3" />
                  <span>Validato da: {getValidatorName(contract.computo_validated_by)}</span>
                </div>
              )}
            </div>
          )}
          
          {/* Note di respinto */}
          {contract.computo_validation_notes && contract.computo_validation_status === 'respinto' && (
            <p className="text-xs text-amber-700 mt-2 bg-amber-50 p-2 rounded">
              <strong>Note:</strong> {contract.computo_validation_notes}
            </p>
          )}
          
          {/* Pulsante VALIDA COMPUTO METRICO */}
          {contract.computo_validation_status !== 'approvato' && (
            <>
              {canValidateComputo ? (
                <Button 
                  size="sm" 
                  className="w-full mt-3 bg-[#0078C8] hover:bg-[#005fa3]"
                  onClick={() => onOpenValidationModal('computo')}
                  data-testid="validate-computo-btn"
                >
                  <Calculator className="w-4 h-4 mr-2" />
                  Valida Computo Metrico
                </Button>
              ) : (
                <div className="mt-3 p-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-500">
                  <div className="flex items-center gap-1.5">
                    <Lock className="w-3.5 h-3.5" />
                    <span>Non hai i permessi per validare il computo metrico</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* === SEZIONE 2: VALIDAZIONE DOCUMENTI AMMINISTRATIVI === */}
        <div className={`p-4 rounded-lg ${adminDocsStatus.bg}`} data-testid="admin-docs-validation">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <ClipboardCheck className={`w-5 h-5 ${adminDocsStatus.color}`} />
              <span className="font-medium text-sm">Documenti Amministrativi</span>
            </div>
            <div className="flex items-center gap-1">
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${adminDocsStatus.bg} ${adminDocsStatus.color}`}>
                {adminDocsStatus.label}
              </span>
              <AdminDocsIcon className={`w-4 h-4 ${adminDocsStatus.color}`} />
            </div>
          </div>
          
          {/* Info validazione se approvato */}
          {contract.admin_docs_validation_status === 'approvato' && (
            <div className="mt-3 p-2 bg-white/60 rounded text-xs space-y-1">
              {contract.admin_docs_validated_at && (
                <div className="flex items-center gap-1.5 text-green-700">
                  <Calendar className="w-3 h-3" />
                  <span>Validato il: {formatValidationDate(contract.admin_docs_validated_at)}</span>
                </div>
              )}
              {contract.admin_docs_validated_by && (
                <div className="flex items-center gap-1.5 text-green-700">
                  <User className="w-3 h-3" />
                  <span>Validato da: {getValidatorName(contract.admin_docs_validated_by)}</span>
                </div>
              )}
            </div>
          )}
          
          {/* Checklist requisiti - visibile solo se NON approvato */}
          {contract.admin_docs_validation_status !== 'approvato' && (
            <div className="mt-3" data-testid="admin-docs-checklist">
              <p className="text-xs font-medium text-gray-600 mb-2">Requisiti per la validazione:</p>
              {loadingReadiness ? (
                <div className="flex items-center gap-2 text-gray-500 text-xs">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Verifica requisiti...
                </div>
              ) : (
                <div className="space-y-1.5">
                  {checklistItems.map((item, idx) => {
                    return (
                      <div 
                        key={idx} 
                        className={`flex items-center gap-2 text-xs ${
                          item.done ? 'text-green-700' : 'text-gray-500'
                        }`}
                      >
                        {item.done ? (
                          <CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                        ) : (
                          <div className="w-3.5 h-3.5 rounded-full border border-gray-400 flex-shrink-0" />
                        )}
                        <span>{item.label}</span>
                        {item.subtitle && (
                          <span className="text-gray-400">{item.subtitle}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          
          {/* Note di respinto */}
          {contract.admin_docs_validation_notes && contract.admin_docs_validation_status === 'respinto' && (
            <p className="text-xs text-amber-700 mt-2 bg-amber-50 p-2 rounded">
              <strong>Note:</strong> {contract.admin_docs_validation_notes}
            </p>
          )}
          
          {/* Pulsante VALIDA DOCUMENTI AMMINISTRATIVI */}
          {contract.admin_docs_validation_status !== 'approvato' && (
            <>
              {/* Warning requisiti mancanti */}
              {!canClickAdminDocsButton && readiness?.blocking_errors?.length > 0 && canValidateAdminDocs && (
                <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                  <div className="flex items-start gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium">Per validare i documenti amministrativi devi prima:</p>
                      <ul className="mt-1 space-y-0.5">
                        {readiness.blocking_errors.map((err, idx) => (
                          <li key={idx}>• {err}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              
              {canValidateAdminDocs ? (
                <Button 
                  size="sm" 
                  className={`w-full mt-3 ${
                    canClickAdminDocsButton 
                      ? 'bg-[#FF7819] hover:bg-[#e06a15]' 
                      : 'bg-gray-300 cursor-not-allowed'
                  }`}
                  onClick={() => canClickAdminDocsButton && onOpenValidationModal('admin_docs')}
                  disabled={!canClickAdminDocsButton}
                  data-testid="validate-admin-docs-btn"
                  title={
                    canClickAdminDocsButton 
                      ? 'Clicca per validare i documenti amministrativi' 
                      : 'Completa i requisiti prima di validare'
                  }
                >
                  <ClipboardCheck className="w-4 h-4 mr-2" />
                  {canClickAdminDocsButton ? 'Valida Documenti Amministrativi' : 'Requisiti incompleti'}
                </Button>
              ) : (
                <div className="mt-3 p-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-500">
                  <div className="flex items-center gap-1.5">
                    <Lock className="w-3.5 h-3.5" />
                    <span>Non hai i permessi per validare i documenti amministrativi</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* === SEZIONE 3: INFO FINALE - TUTTE LE VALIDAZIONI COMPLETATE === */}
        {contract.computo_validation_status === 'approvato' && contract.admin_docs_validation_status === 'approvato' && (
          <div className="text-center p-4 bg-green-100 rounded-lg border border-green-200" data-testid="all-validations-complete">
            <CheckCircle className="w-10 h-10 text-green-600 mx-auto mb-2" />
            <p className="text-sm font-semibold text-green-800">Tutte le validazioni completate!</p>
            <p className="text-xs text-green-700 mt-1">Il contratto è approvato e pronto per l'avvio del progetto.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ContractValidationCard;
